import React from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle } from 'lucide-react';

/**
 * ChunkErrorBoundary — catches runtime/render errors from lazy-loaded pages.
 *
 * Primary purpose: ensure that a failed dynamic import (stale SW cache after
 * a deploy) or any unexpected render crash does NOT leave the user staring at
 * a blank white screen on mobile. We show a clear recovery UI with one-tap
 * reload + cache-clear.
 */
export class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  componentDidMount() {
    // Successful mount — clear any stale reload-loop guard so the NEXT real
    // chunk error (e.g., after a future deploy) can still auto-recover.
    if (sessionStorage.getItem('chunkReloadedOnce')) {
      sessionStorage.removeItem('chunkReloadedOnce');
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    const msg = String(error?.message || error || '');
    // Auto-recover silently for chunk-load errors (stale service worker).
    // Guard against infinite reload loops via sessionStorage.
    const isChunkError =
      /ChunkLoadError/i.test(msg) ||
      /Loading chunk \d+ failed/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg);
    if (isChunkError && !sessionStorage.getItem('chunkReloadedOnce')) {
      sessionStorage.setItem('chunkReloadedOnce', '1');
      this.clearAndReload();
    }
    // If chunkReloadedOnce is already set, we've already tried one reload —
    // show the manual recovery UI instead of spinning forever.
  }

  clearAndReload = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      /* non-fatal */
    }
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="flex flex-col items-center justify-center min-h-[60vh] px-4 py-12 text-center"
        data-testid="chunk-error-boundary"
      >
        <AlertTriangle className="text-amber-500 mb-3" size={40} />
        <h3 className="text-lg font-semibold text-slate-800 mb-1">
          Couldn't load this page
        </h3>
        <p className="text-sm text-slate-500 mb-6 max-w-sm">
          The app was updated in the background. Please reload to pull the latest version.
        </p>
        <div className="flex gap-2">
          <Button
            onClick={this.clearAndReload}
            className="h-10 bg-indigo-600 hover:bg-indigo-700"
            data-testid="chunk-error-reload"
          >
            <RefreshCw size={16} className="mr-2" />
            Reload app
          </Button>
          <Button
            variant="outline"
            onClick={this.handleReset}
            className="h-10"
            data-testid="chunk-error-retry"
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }
}
